import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/Home";
import CategoryList from "@/pages/CategoryList";
import RequestDetail from "@/pages/RequestDetail";
import PublishRequest from "@/pages/PublishRequest";
import MyRequests from "@/pages/MyRequests";
import MyAvailabilities from "@/pages/MyAvailabilities";
import Messages from "@/pages/Messages";
import Conversation from "@/pages/Conversation";
import Zone from "@/pages/Zone";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import PublicProfile from "@/pages/PublicProfile";
import Notifications from "@/pages/Notifications";
import Referral from "@/pages/Referral";
import Directory from "@/pages/Directory";
import MacroNeed from "@/pages/MacroNeed";
import BusinessDetail from "@/pages/BusinessDetail";
import SuggestBusiness from "@/pages/SuggestBusiness";
import PublicDirectory from "@/pages/PublicDirectory";
import Admin from "@/pages/Admin";
import Onboarding from "@/pages/Onboarding";
import AuthCallback from "@/pages/AuthCallback";
import SponsorApply from "@/pages/SponsorApply";
import BusinessLanding from "@/pages/BusinessLanding";
import Locandina from "@/pages/Locandina";
import DemoDesign from "@/pages/DemoDesign";
import CookieConsent from "@/components/CookieConsent";
import { HowItWorks, Terms, Privacy, CookiePolicy, ChiSiamo, Contatti } from "@/pages/StaticPages";

function Loader() {
  return (
    <div className="min-h-screen bg-[#F7F7FB] flex items-center justify-center">
      <p className="font-serif text-3xl text-[#141A3A]">FYDATE</p>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function Public({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user) return <Navigate to={user.onboarded === false ? "/onboarding" : "/"} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <CookieConsent />
          <Routes>
            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/register" element={<Public><Register /></Public>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookie" element={<CookiePolicy />} />
            <Route path="/promuovi" element={<SponsorApply />} />
            <Route path="/per-attivita" element={<BusinessLanding />} />
            <Route path="/locandina" element={<Locandina />} />
            <Route path="/demo-design" element={<DemoDesign />} />
            <Route path="/demo-design/:variant" element={<DemoDesign />} />
            <Route path="/r/:token" element={<Referral />} />
            <Route path="/attivita/:catSlug/:citySlug" element={<PublicDirectory />} />

            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

            <Route path="/" element={<Home />} />
            <Route path="/need/:macro" element={<MacroNeed />} />
            <Route path="/search" element={<Directory />} />
            <Route path="/business/:id" element={<BusinessDetail />} />
            <Route path="/suggest-business" element={<Protected><SuggestBusiness /></Protected>} />
            <Route path="/category/:slug" element={<CategoryList />} />
            <Route path="/request/:id" element={<RequestDetail />} />
            <Route path="/publish/:slug" element={<Protected><PublishRequest /></Protected>} />
            <Route path="/my-requests" element={<Protected><MyRequests /></Protected>} />
            <Route path="/my-availabilities" element={<Protected><MyAvailabilities /></Protected>} />
            <Route path="/messages" element={<Protected><Messages /></Protected>} />
            <Route path="/messages/:id" element={<Protected><Conversation /></Protected>} />
            <Route path="/zone" element={<Protected><Zone /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/u/:id" element={<Protected><PublicProfile /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/come-funziona" element={<HowItWorks />} />
            <Route path="/chi-siamo" element={<ChiSiamo />} />
            <Route path="/contatti" element={<Contatti />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
